import { useState } from "react";
import { Snowflake, Loader2, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MoneyInput } from "@/components/common/MoneyInput";
import { api } from "@/lib/api";
import { mxn, fdate } from "@/lib/format";
import { toast } from "sonner";

/** Panel de diferidos (jun-2026): moratorios congelados + gastos de cobranza diferidos,
 *  con flujo de pago adelantado dedicado (POST /creditos/{id}/pagar-diferidos). */
export function DiferidosPanel({ creditoId, moratoriosDiferidos = [], cargosDiferidos = [], totalMora = 0, totalGc = 0, canPay = false, onPagado }) {
  const total = Math.round((Number(totalMora || 0) + Number(totalGc || 0)) * 100) / 100;
  const [formOpen, setFormOpen] = useState(false);
  const [monto, setMonto] = useState("");
  const [medio, setMedio] = useState("Transferencia SPEI");
  const [busy, setBusy] = useState(false);

  const pagar = async () => {
    const m = Number(String(monto).replace(/,/g, "") || total);
    if (!m || m <= 0) { toast.error("Captura el monto a pagar"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/creditos/${creditoId}/pagar-diferidos`, { monto: m, medio_pago: medio });
      toast.success(`Diferidos cobrados: ${mxn(data.monto)} (recibo ${data.recibo})`);
      setFormOpen(false);
      setMonto("");
      if (onPagado) await onPagado();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar el pago de diferidos");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/40 p-3" data-testid="diferidos-panel">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 font-heading text-sm font-semibold text-sky-900">
          <Snowflake className="h-4 w-4" /> Diferidos al siguiente pago
        </h4>
        <span className="text-xs text-sky-800">
          Pendiente: <b className="font-mono" data-testid="diferidos-total">{mxn(total)}</b>
        </span>
      </div>

      {moratoriosDiferidos.length > 0 && (
        <div className="mb-2 space-y-1" data-testid="diferidos-mora-lista">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-700">Moratorios congelados</p>
          {moratoriosDiferidos.map((m, i) => (
            <div key={`${m.cuota_no}-${i}`}
              className={`flex items-center justify-between rounded-md border bg-white px-2.5 py-1.5 text-xs ${m.pagado ? "border-slate-200 opacity-60" : "border-sky-200"}`}
              data-testid={`diferido-mora-${m.cuota_no}`}>
              <span className="text-slate-600">
                Cuota #{m.cuota_no} · congelado el {fdate(m.fecha_congelado)}
                {m.pagado && <span className="ml-1.5 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">Pagado</span>}
              </span>
              <span className="font-mono text-slate-800">
                {mxn(m.monto_congelado)}
                {!m.pagado && Number(m.pagado_monto || 0) > 0 && <span className="ml-1 text-[10px] text-emerald-600">(abonado {mxn(m.pagado_monto)})</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {cargosDiferidos.length > 0 && (
        <div className="mb-2 space-y-1" data-testid="diferidos-gc-lista">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-700">Gastos de cobranza diferidos</p>
          {cargosDiferidos.map((c) => {
            const totalC = Number(c.monto || 0) + Number(c.iva || 0);
            const pagadoC = Number(c.pagado_monto || 0) + Number(c.pagado_iva || 0);
            return (
              <div key={c.id} className="flex items-center justify-between rounded-md border border-sky-200 bg-white px-2.5 py-1.5 text-xs" data-testid={`diferido-gc-${c.id}`}>
                <span className="flex items-center gap-1.5 text-slate-600">
                  <Receipt className="h-3 w-3" /> {fdate(c.fecha)} · diferido el {fdate(c.diferido_fecha)}
                </span>
                <span className="font-mono text-slate-800">
                  {mxn(c.monto)}{Number(c.iva || 0) > 0 && ` + IVA ${mxn(c.iva)}`}
                  {pagadoC > 0 && <span className="ml-1 text-[10px] text-emerald-600">(pagado {mxn(pagadoC)} / {mxn(totalC)})</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[11px] text-sky-800">
        El moratorio se <b>congeló</b> al liquidarse la cuota atrasada (dejó de crecer) y se cobra junto con el siguiente pago, o por adelantado aquí.
      </p>

      {canPay && total > 0 && !formOpen && (
        <Button data-testid="pagar-diferidos-btn" size="sm" variant="outline"
          className="mt-2 border-sky-300 text-sky-800 hover:bg-sky-100"
          onClick={() => { setMonto(String(total)); setFormOpen(true); }}>
          <Snowflake className="mr-1.5 h-3.5 w-3.5" /> Pagar diferidos por adelantado
        </Button>
      )}
      {canPay && formOpen && (
        <div className="mt-2 grid grid-cols-2 gap-2 rounded-md border border-sky-200 bg-white p-2.5">
          <div>
            <Label className="mb-1 block text-xs">Monto (máx. {mxn(total)})</Label>
            <MoneyInput data-testid="pagar-diferidos-monto" value={monto} onChange={setMonto} />
          </div>
          <div>
            <Label className="mb-1 block text-xs">Medio de pago</Label>
            <Select value={medio} onValueChange={setMedio}>
              <SelectTrigger data-testid="pagar-diferidos-medio"><SelectValue /></SelectTrigger>
              <SelectContent>{["Transferencia SPEI", "Efectivo", "Domiciliación", "Cheque", "Tarjeta"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2 flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setFormOpen(false)} disabled={busy}>Cancelar</Button>
            <Button data-testid="pagar-diferidos-confirmar" size="sm" onClick={pagar} disabled={busy} className="bg-sky-700 hover:bg-sky-800">
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null} Cobrar diferidos
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
